<template>
    <div v-if="data != null && data.length > 0">
        <div ref="treeContainer" :class="{'collapsed-height': addCollapsedClass, 'expanded-height': addExpandedClass}">
            <b-form-group class="pb-0 mb-1" v-if="showSearchFilter">
                <p-input name="searchInput" :placeholder="filterPlaceHolder" size="sm" class="me-2" v-model="userFilter" type="search" />
            </b-form-group>
            <div class="tree-container">
                <tree-node
                    v-for="node in filteredNodes"
                    :key="node.value || node.text"
                    :node="node"
                    :level="0"
                    @selection-changed="onSelectionChanged"
                />
            </div>
        </div>
        <div class="text-center mt-1">
            <b-button variant="light" size="sm" class="py-0"
                v-if="addCollapsedClass && tallEnoughForExpansion"
                @click="expanded = true">
                Show More <b-icon-chevron-down></b-icon-chevron-down>
            </b-button>
            <b-button variant="light" size="sm" class="py-0"
                v-if="addExpandedClass"
                @click="expanded = false">
                Show Less <b-icon-chevron-up></b-icon-chevron-up>
            </b-button>
        </div>
    </div>
</template>

<script>
import { h } from 'vue';
import { textIncludes } from "@/components/Common/Formatters.js";

const TreeNode = {
    name: 'TreeNode',
    props: {
        node: Object,
        level: { type: Number, default: 0 }
    },
    emits: ['selection-changed'],
    computed: {
        hasChildren() {
            return this.node.children && this.node.children.length > 0;
        },
        isSelected() {
            return !!this.node.selected;
        },
        isIndeterminate() {
            if (!this.hasChildren || this.isSelected) return false;
            return this.hasAnySelectedDescendant(this.node.children);
        },
        isOpen: {
            get() { return !!this.node.opened; },
            set(v) { this.node.opened = v; }
        }
    },
    methods: {
        toggle() {
            if (this.hasChildren) {
                this.isOpen = !this.isOpen;
            }
        },
        toggleCheck() {
            this.node.selected = !this.node.selected;
            if (this.hasChildren) {
                this.setChildrenSelected(this.node.children, this.node.selected);
            }
            this.$emit('selection-changed');
        },
        setChildrenSelected(children, selected) {
            children.forEach(child => {
                child.selected = selected;
                if (child.children) {
                    this.setChildrenSelected(child.children, selected);
                }
            });
        },
        hasAnySelectedDescendant(children) {
            for (const c of children) {
                if (c.selected) return true;
                if (c.children && c.children.length && this.hasAnySelectedDescendant(c.children)) return true;
            }
            return false;
        },
        refreshSelectedFromChildren() {
            if (this.hasChildren) {
                this.node.selected = this.node.children.every(c => c.selected);
            }
        }
    },
    render() {
        const toggleIcon = this.hasChildren
            ? h('span', { class: 'tree-toggle', onClick: (e) => { e.stopPropagation(); this.toggle(); } }, [
                h('i', { class: this.isOpen ? 'bi bi-chevron-down' : 'bi bi-chevron-right', style: 'font-size: 0.75rem; width: 16px; display: inline-block;' })
            ])
            : h('span', { style: 'width: 16px; display: inline-block;' });

        const checkbox = h('span', { class: ['tree-checkbox', { checked: this.isSelected, indeterminate: this.isIndeterminate }] },
            this.isSelected
                ? [h('i', { class: 'bi bi-check', style: 'font-size: 0.8rem;' })]
                : this.isIndeterminate
                    ? [h('span', { class: 'tree-dash' })]
                    : []
        );

        const text = h('span', { class: 'tree-text' }, this.node.text);

        const row = h('div', { class: 'tree-row', onClick: () => this.toggleCheck() }, [toggleIcon, checkbox, text]);

        const children = (this.hasChildren && this.isOpen)
            ? h('div', {},
                this.node.children.map(child =>
                    h(TreeNode, {
                        key: child.value || child.text,
                        node: child,
                        level: this.level + 1,
                        onSelectionChanged: () => {
                            this.refreshSelectedFromChildren();
                            this.$emit('selection-changed');
                        }
                    })
                )
            )
            : null;

        return h('div', { class: 'tree-node', style: { paddingLeft: (this.level * 18) + 'px' } }, [row, children]);
    }
};

export default {
    name: 'p-tree',
    components: {
        TreeNode
    },
    props: {
        data: {
            type: Array,
            default: () => []
        },
        expandable: {
            type: Boolean,
            default: true
        },
        includeSearchFilter: {
            type: Boolean,
            default: false
        },
        filterPlaceHolder: {
            type: String,
            default: "Filter"
        }
    },
    data() {
        return {
            expanded: false,
            tallEnoughForExpansion: false,
            userFilter: '',
            resizeObserver: null
        };
    },
    computed: {
        showSearchFilter() {
            return this.includeSearchFilter && this.data.length > 1;
        },
        addCollapsedClass() {
            return this.expandable && !this.expanded;
        },
        addExpandedClass() {
            return this.expandable && this.expanded;
        },
        filteredNodes() {
            if (!this.userFilter) return this.data;
            return this.data.filter(node => textIncludes(node.text, this.userFilter));
        }
    },
    methods: {
        onSelectionChanged() {
            this.$emit('selection-change', this.data);
            this.$nextTick(() => {
                this.checkExpansion();
            });
        },
        checkExpansion() {
            const container = this.$refs.treeContainer;
            if (container) {
                this.tallEnoughForExpansion = container.scrollHeight > container.clientHeight;
            }
        },
        expandAll(expand) {
            const walk = (nodes) => {
                if (!nodes) return;
                nodes.forEach(n => {
                    if (n.children && n.children.length) {
                        n.opened = expand;
                        walk(n.children);
                    }
                });
            };
            walk(this.data);
        },
        toggle() {
            //after the expansion is rendered see if there is overflow
            setTimeout(() => {
                let container = this.$refs.treeContainer;
                this.tallEnoughForExpansion = container.scrollHeight > container.clientHeight;
            });
        }
    },
    mounted() {
        this.$nextTick(() => this.checkExpansion());
        // The tree is often rendered inside a hidden modal, so the container has
        // 0 height at mount. Watch for size changes (including when the modal
        // opens) and re-run the overflow check.
        if (typeof ResizeObserver !== 'undefined' && this.$refs.treeContainer) {
            this.resizeObserver = new ResizeObserver(() => this.checkExpansion());
            this.resizeObserver.observe(this.$refs.treeContainer);
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
};
</script>

<style scoped lang="scss">
.collapsed-height {
    max-height: 180px;
    overflow: auto;
}

.expanded-height {
    max-height: 450px;
    overflow: auto;
}

fieldset {
    border-bottom: none;
}
</style>

<style lang="scss">
@import "@/styles/app/common/variables.scss";

.tree-container {
    font-size: 0.875rem;
}

.tree-row {
    display: flex;
    align-items: center;
    padding: 3px 6px;
    cursor: pointer;
    border-radius: 3px;

    &:hover {
        background-color: $gray-lighter;
    }

}

.tree-toggle {
    cursor: pointer;
    color: $body-color;
}

.tree-checkbox {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    border: 1.5px solid #999;
    border-radius: 2px;
    margin-right: 6px;
    flex-shrink: 0;
    background-color: white;

    &.checked {
        background-color: $primary;
        border-color: $primary;
        color: white;
    }

    &.indeterminate {
        background-color: $primary;
        border-color: $primary;
        color: white;
    }
}

.tree-dash {
    display: block;
    width: 8px;
    height: 2px;
    background-color: white;
    border-radius: 1px;
}

.tree-text {
    user-select: none;
}
</style>
